/**
 * The Opportunity state machine.
 *
 * A PURE function, imported by BOTH halves, so the client greys out exactly
 * the buttons the server refuses. CLAUDE.md rule 14 in one file: the version
 * of the "hide the button" bug that survives a fix is the one where the button
 * is hidden and the endpoint still accepts the request.
 *
 * ── Two axes, not one ────────────────────────────────────────────────────────
 *
 * `stage` is the ordered machine — seventeen values, where you ARE.
 * `disposition` is orthogonal — six values, HOW IT ENDED, or `open`.
 *
 * One column holding all twenty-three values loses the stage you were in when
 * you passed, and the decision log plus the learned preferences it feeds are
 * built entirely on knowing whether you passed at `reviewing` (too expensive
 * on sight) or at `inspection` (rotten floor). Those are different lessons.
 *
 * §11's `SOLD` is renamed `lost` on this axis. `sold` survives only as a
 * `listings.availability` value. Two axes using one word for opposite outcomes
 * — "someone else bought it" versus "the posting says sold" — is a bug waiting
 * for a tired evening, and `sold` sits one row away from `purchased`.
 *
 * ── An illegal transition is a REFUSAL, never a throw ────────────────────────
 *
 * §5 is explicit: the endpoint returns 200 with `{ ok: false, code, message }`
 * and changes nothing — no row, no decision, no audit entry, no event. This
 * function returns that shape. It never throws, so it is safe to call inside a
 * render to decide whether a button is live.
 *
 * ── §3.4, §3.6, §5.1 ─────────────────────────────────────────────────────────
 */

/**
 * Seventeen ordered stages. `stageRank` is the index — the order IS the
 * meaning, so nothing may be inserted in the middle without thinking about
 * every `<`/`>` comparison in the file.
 */
export const STAGES = [
  /** Intake landed. Nobody has looked. */
  'new',
  /** Someone is looking now. */
  'reviewing',
  /** Worth pursuing. */
  'interested',
  /** Reaching out. */
  'contact_seller',
  /** Ball is in their court. */
  'waiting_seller',
  /** Gathering facts, filling the checklist. */
  'qualifying',
  /** Getting a real number from a hauler. */
  'transport_quote',
  /** Going to look at it. */
  'inspection',
  /** Offers moving. */
  'negotiating',
  /** Would buy it. Sets `is_finalist = 1`, permanently. */
  'finalist',
  /** Agreed, not paid. */
  'purchase_pending',
  /** Paid. **Not success.** */
  'purchased',
  /** Hauler and barge booked. */
  'transport_scheduled',
  /** Moving. */
  'in_transit',
  /** On the island. */
  'delivered',
  /** **The success condition.** */
  'placed_on_land',
  /** Actuals reconciled, disposition `complete`. */
  'project_complete',
] as const

export type Stage = (typeof STAGES)[number]

export const DISPOSITIONS = ['open', 'passed', 'lost', 'scam', 'duplicate', 'complete'] as const
export type Disposition = (typeof DISPOSITIONS)[number]

/**
 * The dispositions that take an opportunity off the open feed.
 *
 * `scam` and `duplicate` are in this list and are **not terminal** — an
 * auto-attach at 85 that turns out to be two near-identical popups from one
 * dealer must be reversible. A row that can never be recovered is deleted in
 * every sense that matters to the person looking at it.
 */
export const CLOSED_DISPOSITIONS = ['passed', 'lost', 'scam', 'duplicate'] as const
export type ClosedDisposition = (typeof CLOSED_DISPOSITIONS)[number]

/** The only disposition that cannot be left. */
export const TERMINAL_DISPOSITION: Disposition = 'complete'

export function isStage(value: unknown): value is Stage {
  return typeof value === 'string' && (STAGES as readonly string[]).includes(value)
}

export function isDisposition(value: unknown): value is Disposition {
  return typeof value === 'string' && (DISPOSITIONS as readonly string[]).includes(value)
}

/** The index in `STAGES`. `-1` for anything that is not a stage. */
export function stageRank(stage: Stage | string): number {
  return (STAGES as readonly string[]).indexOf(stage)
}

/** The rank at and past which the machine becomes monotonic. */
export const RANK_PURCHASED = stageRank('purchased')

/**
 * Five lanes, derived in TypeScript and **not stored**.
 *
 * Seventeen stages plus five terminal dispositions is twenty-two columns,
 * which is not a board on any screen. Keeping the grouping in code rather than
 * in a column means regrouping the pipeline is a one-line change instead of a
 * forward-only migration that locks a presentation decision into the schema.
 *
 * The lanes are a grouping by PURPOSE, and they are the reason `landing` holds
 * six stages while `deciding` holds two: the post-purchase stages are things
 * that happened, and you watch them rather than work them.
 */
export const LANES = ['triage', 'pursuing', 'diligence', 'deciding', 'landing'] as const
export type Lane = (typeof LANES)[number]

export const LANE_OF: Record<Stage, Lane> = {
  new: 'triage',
  reviewing: 'triage',
  interested: 'pursuing',
  contact_seller: 'pursuing',
  waiting_seller: 'pursuing',
  qualifying: 'pursuing',
  transport_quote: 'diligence',
  inspection: 'diligence',
  negotiating: 'diligence',
  finalist: 'deciding',
  purchase_pending: 'deciding',
  purchased: 'landing',
  transport_scheduled: 'landing',
  in_transit: 'landing',
  delivered: 'landing',
  placed_on_land: 'landing',
  project_complete: 'landing',
}

/** The stages in a lane, in pipeline order. */
export function stagesInLane(lane: Lane): Stage[] {
  return STAGES.filter((s) => LANE_OF[s] === lane)
}

/**
 * `opportunity_decisions.kind`. `merge` and `unmerge` are written by
 * `duplicates.ts`, not by this machine, and are listed so the union is the
 * column's whole CHECK constraint rather than a subset of it.
 */
export const DECISION_KINDS = ['stage', 'disposition', 'merge', 'unmerge', 'reopen'] as const
export type DecisionKind = (typeof DECISION_KINDS)[number]

/** `decision_reasons.kind`. */
export const REASON_KINDS = ['pass', 'finalist', 'lost', 'scam', 'duplicate', 'reopen'] as const
export type ReasonKind = (typeof REASON_KINDS)[number]

/*
 * The seeded reason codes (0112), mirrored here so a client can render the
 * chips before its first fetch returns.
 *
 * `decision_reasons` IS the authority: it is admin-editable, it carries the
 * Spanish label, and it carries `active`. A handler validates a submitted code
 * against the TABLE — never against these constants — because a reason
 * deactivated in the console must stop being offered, and a constant compiled
 * into a Worker cannot know that.
 */

/** §M's twelve pass reasons, in seeded sort order. */
export const PASS_REASONS = [
  'pass_too_expensive',
  'pass_transport_too_expensive',
  'pass_bad_condition',
  'pass_title_issue',
  'pass_too_large',
  'pass_too_small',
  'pass_poor_access',
  'pass_seller_unreliable',
  'pass_sold',
  'pass_scam_concern',
  'pass_found_better',
  'pass_other',
] as const

/** §M's eight finalist reasons. */
export const FINALIST_REASONS = [
  'finalist_excellent_landed_cost',
  'finalist_nearby',
  'finalist_good_condition',
  'finalist_easy_transport',
  'finalist_excellent_size',
  'finalist_utilities_included',
  'finalist_strong_seller',
  'finalist_move_in_ready',
] as const

export const LOST_REASONS = [
  'lost_sold_to_other',
  'lost_seller_withdrew',
  'lost_price_raised',
  'lost_no_response',
  'lost_other',
] as const

export const SCAM_REASONS = [
  'scam_advance_payment',
  'scam_no_inspection',
  'scam_stolen_photos',
  'scam_impossible_price',
  'scam_other',
] as const

export const DUPLICATE_REASONS = [
  'duplicate_same_listing',
  'duplicate_reposted',
  'duplicate_same_seller',
] as const

export const REOPEN_REASONS = [
  'reopen_correction',
  'reopen_seller_returned',
  'reopen_new_information',
] as const

/** The seeded codes for a reason kind, for rendering a picker offline. */
export const SEEDED_REASONS: Record<ReasonKind, readonly string[]> = {
  pass: PASS_REASONS,
  finalist: FINALIST_REASONS,
  lost: LOST_REASONS,
  scam: SCAM_REASONS,
  duplicate: DUPLICATE_REASONS,
  reopen: REOPEN_REASONS,
}

/**
 * §5's five refusal codes, and only these five.
 *
 * `conflict` and `not_found` cannot be produced by this pure function — a lost
 * race and a missing row are facts about the database. They are in the union
 * because the endpoint returns one union and the client switches on it once.
 */
export const TRANSITION_CODES = [
  'illegal',
  'conflict',
  'reason_required',
  'evidence_required',
  'not_found',
] as const
export type TransitionCode = (typeof TRANSITION_CODES)[number]

/** Where the opportunity is now. The guard values for the conditional UPDATE. */
export interface PipelineState {
  stage: Stage
  disposition: Disposition
  /**
   * Set on entering `finalist` and **never cleared by a forward move**.
   *
   * Without it, advancing a finalist to `purchase_pending` silently empties
   * the comparison screen the flag exists to fill.
   */
  isFinalist?: boolean
}

/** What the caller is asking for. Every field beyond the target is evidence. */
export interface TransitionRequest {
  /** Omitted means "stay in this stage". */
  stage?: Stage
  /** Omitted means "leave the disposition alone". */
  disposition?: Disposition
  /** A `decision_reasons.code`. The handler checks it is real and `active`. */
  reasonCode?: string | null
  /**
   * A data correction, not a business move. Only meaningful for a backward
   * move at or past `purchased`, and only honoured with `settings.write`.
   */
  correction?: boolean
  /**
   * Does the caller hold `settings.write`? Passed in rather than looked up
   * because this file is `shared/` and may not import `roles.ts`.
   */
  mayConfigure?: boolean
  /** Evidence for entering `purchased`. Integer cents. */
  purchasePriceCents?: number | null
  /** Evidence for entering `purchased`. `YYYY-MM-DD`, from `businessDate`. */
  purchasedOn?: string | null
  /**
   * Evidence for entering `placed_on_land`: how many non-zero cost lines still
   * have no recorded actual. Zero means the ledger is reconciled.
   */
  outstandingCostLines?: number
  /** The escape hatch for `placed_on_land`, which needs its own reason. */
  actualsWaived?: boolean
  actualsWaivedReason?: string | null
}

export interface TransitionOk {
  ok: true
  /** The state to write. Apply it with a guarded UPDATE against `from`. */
  next: Required<PipelineState>
  /** What the `opportunity_decisions` row records. */
  decisionKind: DecisionKind
  /** Which `decision_reasons.kind` the supplied code must belong to, if any. */
  reasonKind: ReasonKind | null
  /** True when this is the move that sets `is_finalist` for the first time. */
  entersFinalist: boolean
}

export interface TransitionRefused {
  ok: false
  code: TransitionCode
  /**
   * A sentence for a person, in English. Not a key: this reaches an owner in a
   * snackbar, and a body of `pipeline.illegal` shown verbatim is worse than an
   * untranslated sentence.
   */
  message: string
}

export type TransitionOutcome = TransitionOk | TransitionRefused

const refuse = (code: TransitionCode, message: string): TransitionRefused => ({ ok: false, code, message })

/** Exactly ten characters, `YYYY-MM-DD` — the shape `businessDate` returns. */
function isBusinessDate(value: unknown): value is string {
  return typeof value === 'string' && value.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(value)
}

function hasReason(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim() !== ''
}

/**
 * Which kind of reason this move needs, or `null`.
 *
 * §M asks for a reason on Pass and on Finalist, and §N — the learned
 * preferences — is built entirely on counting them. **A reason nobody types is
 * a feature that does not exist**, so it is required, not encouraged. Marking
 * `scam` is the most consequential write in the machine and was the one the
 * drafts left unreasoned.
 */
export function requiresReason(from: PipelineState, to: TransitionRequest): ReasonKind | null {
  const nextStage = to.stage ?? from.stage
  const nextDisposition = to.disposition ?? from.disposition

  if (nextDisposition !== from.disposition) {
    switch (nextDisposition) {
      case 'open':
        return 'reopen'
      case 'passed':
        return 'pass'
      case 'lost':
        return 'lost'
      case 'scam':
        return 'scam'
      case 'duplicate':
        return 'duplicate'
      /*
       * `complete` is a reconciliation, not a judgement: it is set by the act
       * of entering `project_complete` with the actuals in. There is nothing
       * to ask a person, and inventing a picker for it would train them to
       * dismiss the pickers that matter.
       */
      case 'complete':
        return null
    }
  }

  // Entering the finalist stage — the durable set §18 compares side by side.
  if (nextStage === 'finalist' && from.stage !== 'finalist') return 'finalist'

  // A correction back through the purchase wall is a `reopen` and says why.
  if (stageRank(from.stage) >= RANK_PURCHASED && stageRank(nextStage) < stageRank(from.stage)) {
    return 'reopen'
  }

  return null
}

/** Rule 9: is this the move that enters `purchased`? */
export function requiresPurchaseEvidence(from: PipelineState, to: TransitionRequest): boolean {
  const nextStage = to.stage ?? from.stage
  return nextStage === 'purchased' && from.stage !== 'purchased'
}

/** Rule 10: is this the move that enters `placed_on_land`? */
export function requiresPlacementEvidence(from: PipelineState, to: TransitionRequest): boolean {
  const nextStage = to.stage ?? from.stage
  return nextStage === 'placed_on_land' && from.stage !== 'placed_on_land'
}

/**
 * The whole machine. §5's eleven rules in §5's evaluation order.
 *
 * Rule 11 — the guarded UPDATE matching zero rows — is NOT here, because it is
 * a fact about the database. The caller runs the guarded UPDATE **alone**,
 * checks `meta.changes === 1`, and only then writes the decision row in a
 * second batch. D1's batch is one transaction that rolls back on an ERROR, not
 * on zero affected rows: a guarded UPDATE that matches nothing is a perfectly
 * successful statement, so batching the decision row with it commits a reason
 * for a stage change that did not happen.
 */
export function canTransition(from: PipelineState, to: TransitionRequest): TransitionOutcome {
  if (!isStage(from.stage)) return refuse('illegal', `"${String(from.stage)}" is not a stage.`)
  if (!isDisposition(from.disposition)) {
    return refuse('illegal', `"${String(from.disposition)}" is not a disposition.`)
  }
  if (to.stage !== undefined && !isStage(to.stage)) {
    return refuse('illegal', `"${String(to.stage)}" is not a stage.`)
  }
  if (to.disposition !== undefined && !isDisposition(to.disposition)) {
    return refuse('illegal', `"${String(to.disposition)}" is not a disposition.`)
  }

  const nextStage: Stage = to.stage ?? from.stage
  let nextDisposition: Disposition = to.disposition ?? from.disposition

  const fromRank = stageRank(from.stage)
  const toRank = stageRank(nextStage)

  /* ── Rule 7a. `complete` is terminal. ─────────────────────────────────── */
  if (from.disposition === 'complete') {
    return refuse('illegal', 'This project is complete. A completed project cannot be reopened.')
  }

  /*
   * ── Rule 7b. `complete` is set ONLY by entering `project_complete` from
   * `placed_on_land`.
   *
   * This is the one place where §5 is stricter than §3.6's "any forward move
   * is legal, including skipping", and §5 wins: `project_complete` means the
   * actuals are reconciled against a structure that is on the land. Skipping
   * to it from `reviewing` would mark a project complete that never happened,
   * and the disposition it sets can never be undone.
   */
  if (nextStage === 'project_complete' && from.stage !== 'project_complete') {
    if (from.stage !== 'placed_on_land') {
      return refuse(
        'illegal',
        'A project is completed from "placed on land", once the actuals are reconciled — not from ' +
          `"${from.stage}".`,
      )
    }
    nextDisposition = 'complete'
  } else if (nextDisposition === 'complete') {
    return refuse(
      'illegal',
      'The "complete" disposition is set by entering the "project complete" stage, never on its own.',
    )
  }

  const stageChanged = nextStage !== from.stage
  const dispositionChanged = nextDisposition !== from.disposition

  if (!stageChanged && !dispositionChanged) {
    return refuse('illegal', 'Nothing would change.')
  }

  let decisionKind: DecisionKind = stageChanged ? 'stage' : 'disposition'

  /* ── Rules 1–4. The stage axis. ───────────────────────────────────────── */
  if (stageChanged && toRank < fromRank) {
    // Rule 2: backward is ordinary life before the purchase wall. A seller
    // goes quiet; an inspection fails.
    if (fromRank >= RANK_PURCHASED) {
      // Rules 3 and 4. Past `purchased` the stages record physical events that
      // happened, and you cannot un-deliver a trailer — but a typo at 11pm is
      // also real, so the correction path exists, is gated higher, and is
      // written down AS a correction.
      if (!to.correction) {
        return refuse(
          'illegal',
          `"${from.stage}" records something that already happened and cannot simply be moved back. ` +
            'Reopen it as a correction if it was recorded in error.',
        )
      }
      if (!to.mayConfigure) {
        return refuse(
          'illegal',
          'Correcting a stage at or past "purchased" needs an administrator.',
        )
      }
      decisionKind = 'reopen'
    }
  }

  /* ── Rules 5 and 6. The disposition axis. ─────────────────────────────── */
  if (dispositionChanged && nextDisposition !== 'complete') {
    const leavingOpen = from.disposition === 'open'
    const returningToOpen = nextDisposition === 'open'

    if (leavingOpen) {
      // Rule 5: open → {passed, lost, scam, duplicate}, from ANY stage.
      decisionKind = 'disposition'
    } else if (returningToOpen) {
      // Rule 6: a closed disposition reopens. §3.25's UNDO is this, not a
      // delete — a second, forward decision.
      decisionKind = 'reopen'
    } else {
      return refuse(
        'illegal',
        `Move it back to open before marking it "${nextDisposition}", so both decisions are recorded.`,
      )
    }
  }

  /* ── Rule 8. A reason, or nothing happens. ────────────────────────────── */
  const reasonKind = requiresReason(from, { ...to, stage: nextStage, disposition: nextDisposition })
  if (reasonKind !== null && !hasReason(to.reasonCode)) {
    return refuse('reason_required', `This needs a reason. Pick one of the "${reasonKind}" reasons.`)
  }

  /* ── Rule 9. Entering `purchased` needs the price and the date. ───────── */
  if (requiresPurchaseEvidence(from, { stage: nextStage })) {
    const price = to.purchasePriceCents
    if (typeof price !== 'number' || !Number.isSafeInteger(price) || price < 0) {
      return refuse('evidence_required', 'Record what you actually paid, in whole cents, before marking it purchased.')
    }
    if (!isBusinessDate(to.purchasedOn)) {
      return refuse('evidence_required', 'Record the date you paid before marking it purchased.')
    }
  }

  /* ── Rule 10. Entering `placed_on_land` needs the actuals, or a waiver. ─ */
  if (requiresPlacementEvidence(from, { stage: nextStage })) {
    const outstanding = to.outstandingCostLines ?? 0
    if (outstanding > 0) {
      if (!to.actualsWaived) {
        return refuse(
          'evidence_required',
          `${outstanding} cost line${outstanding === 1 ? '' : 's'} still ` +
            'have no recorded actual. Record them, or waive them with a reason.',
        )
      }
      if (!hasReason(to.actualsWaivedReason)) {
        return refuse('evidence_required', 'Say why the remaining cost actuals are being waived.')
      }
    }
  }

  const entersFinalist = nextStage === 'finalist' && from.stage !== 'finalist'

  return {
    ok: true,
    next: {
      stage: nextStage,
      disposition: nextDisposition,
      // Never cleared by a forward move.
      isFinalist: (from.isFinalist ?? false) || entersFinalist,
    },
    decisionKind,
    reasonKind,
    entersFinalist,
  }
}

/**
 * The stages a caller could legally move to right now, for greying out chips.
 *
 * Deliberately answers the STAGE question only, with the evidence gates
 * satisfied, because a Board sheet has to render before it knows whether the
 * cost ledger is reconciled. The endpoint is still the authority — this only
 * decides what is worth offering.
 */
export function reachableStages(from: PipelineState, mayConfigure = false): Stage[] {
  return STAGES.filter((stage) => {
    if (stage === from.stage) return false
    const outcome = canTransition(from, {
      stage,
      mayConfigure,
      correction: mayConfigure,
      // Evidence and reasons are collected by the sheet AFTER the chip is
      // tapped, so they are satisfied here to keep the chip offerable.
      reasonCode: 'probe',
      purchasePriceCents: 0,
      purchasedOn: '0000-00-00',
      outstandingCostLines: 0,
    })
    return outcome.ok
  })
}
